import { Epg as EpgContainer, Layout } from 'planby';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isBefore, subHours } from 'date-fns';
import type { EpgChannel, EpgProgram } from '@jwp/ott-common/types/epg';
import type { Config } from '@jwp/ott-common/types/config';
import usePlanByEpg from '@jwp/ott-hooks-react/src/usePlanByEpg';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';

import IconButton from '../IconButton/IconButton';
import Button from '../Button/Button';
import EpgChannelItem from '../EpgChannel/EpgChannelItem';
import EpgProgramItem from '../EpgProgramItem/EpgProgramItem';
import EpgTimeline from '../EpgTimeline/EpgTimeline';
import Spinner from '../Spinner/Spinner';
import Icon from '../Icon/Icon';
import { useTheme } from '../../../../../ThemeContext';
import { useAccountStore } from '../../../../common/src/stores/AccountStore';

import styles from './Epg.module.scss';

type Props = {
  channels: EpgChannel[];
  onChannelClick: (channelId: string) => void;
  onProgramClick: (programId: string, channelId: string) => void;
  selectedChannel: EpgChannel | undefined;
  program: EpgProgram | undefined;
  config: Config;
};

export default function Epg({ channels, selectedChannel, onChannelClick, onProgramClick, program, config }: Props) {
  const breakpoint = useBreakpoint();
  const { t } = useTranslation('common');
  const { universalStyles } = useTheme();
  const { isCatchUpHoursEnabled } = useAppConfig();
  const isMobile = breakpoint < Breakpoint.sm;
  const sidebarWidth = 66;
  // the subtracted value is used for spacing in the sidebar
  const channelItemWidth = isMobile ? sidebarWidth - 10 : sidebarWidth - 24;
  const itemHeight = 60;
  const { click } = useContentStateStore.getState();
  const screenClass = click?.label;
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();

  // Epg
  const { getEpgProps, getLayoutProps, onScrollToNow, onScrollLeft, onScrollRight } = usePlanByEpg({
    channels,
    sidebarWidth,
    itemHeight,
    highlightColor: universalStyles.live.backgroundColor,
    backgroundColor: universalStyles.bodyBackgroundColor.backgroundColor,
  });

  const catchupHoursDict = useMemo(() => Object.fromEntries(channels.map((channel) => [channel.id, channel.catchupHours])), [channels]);
  const titlesDict = useMemo(() => Object.fromEntries(channels.map((channel) => [channel.id, channel.title])), [channels]);
  const DayComponent = () => {
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDate = new Date();
    const currentDay = daysOfWeek[currentDate.getDay()];
    return currentDay.substring(0, 3);
  };

  useEffect(() => {
    sendGAEvent('screen_view', {
      firebase_screen_class: screenClass,
      firebase_screen: `${program?.title}`,
      device_type: deviceType,
      userid_no: userId,
    });
  }, [program?.title]);
  
  return (
    <div className={styles.epg}>
      <div className={styles.timelineControl}>
        <Button className={styles.timelineNowButton} variant="outlined" label={t('now')} color="nowBUtton" onClick={onScrollToNow} size="small" />
        <IconButton className={styles.leftControl} aria-label={t('slide_left')} onClick={() => onScrollLeft()}>
          <Icon icon={ChevronLeft} />
        </IconButton>
        <IconButton className={styles.rightControl} aria-label={t('slide_right')} onClick={() => onScrollRight()}>
          <Icon icon={ChevronRight} />
        </IconButton>
      </div>
      <div className={styles.day}>{DayComponent()}</div>
      <EpgContainer {...getEpgProps()} loader={<Spinner className={styles.epgSpinner} />}>
        <Layout
          {...getLayoutProps()}
          renderTimeline={(props) => <EpgTimeline {...props} />}
          renderChannel={({ channel: epgChannel }) => {
            return (
              <EpgChannelItem
                key={epgChannel.uuid}
                channel={epgChannel}
                channelItemWidth={channelItemWidth}
                sidebarWidth={sidebarWidth}
                onClick={(toChannel: any) => {
                  onChannelClick(toChannel.uuid);
                  onScrollToNow();
                }}
                title={titlesDict[epgChannel.uuid] || ''}
                isActive={selectedChannel?.id === epgChannel.uuid}
              />
            );
          }}
          renderProgram={({ program: programItem, isBaseTimeFormat }) => {
            const catchupHours = catchupHoursDict[programItem.data.channelUuid];
            const disabled = isBefore(new Date(programItem.data.since), subHours(new Date(), catchupHours));

            return (
              <EpgProgramItem
                key={programItem.data.id}
                program={programItem}
                disabled={isCatchUpHoursEnabled ? disabled : false}
                onClick={(program: any) => (isCatchUpHoursEnabled ? !disabled : true) && onProgramClick(program.data.id, program.data.channelUuid)}
                isActive={program?.id === programItem.data.id}
                compact={isMobile}
                isBaseTimeFormat={isBaseTimeFormat}
              />
            );
          }}
        />
      </EpgContainer>
    </div>
  );
}
